import { useState, useEffect } from 'react';
import { Plus, ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { useProjectStore } from '@/stores/useProject.store';
import { useUserStore } from '@/stores/useUser.store';
import { Input } from '@/components/shadcn-ui/input';
import { Textarea } from '@/components/shadcn-ui/textarea';
import { toast } from 'sonner';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10}$/;

const ProjectLinkStep = ({ onSubmit, onBack, permitChatId }) => {
  const userRole = useUserStore(state => state.userRole);
  const projects = useProjectStore(state => state.projects);
  const customers = useProjectStore(state => state.customers);
  const getProjects = useProjectStore(state => state.getProjects);
  const getCustomers = useProjectStore(state => state.getCustomers);
  const createCustomer = useProjectStore(state => state.createCustomer);
  const getProjectsByCustomer = useProjectStore(state => state.getProjectsByCustomer);

  const isContractor = userRole === 'contractor';

  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(isContractor ? 'customers' : 'projects');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [projectForm, setProjectForm] = useState({ name: '', description: '' });
  const [customerForm, setCustomerForm] = useState({
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    property_address: '',
    permit_chat_id: permitChatId,
  });
  const [touched, setTouched] = useState({ owner_email: false, owner_phone: false });

  const isEmailValid = emailRegex.test(customerForm.owner_email);
  const isPhoneValid = phoneRegex.test(customerForm.owner_phone);
  const isCustomerFormValid =
    customerForm.owner_name.trim() &&
    customerForm.property_address.trim() &&
    isEmailValid &&
    isPhoneValid;

  useEffect(() => {
    if (isContractor) {
      getCustomers();
    } else {
      getProjects();
    }
  }, [isContractor, getCustomers, getProjects]);

  const handleSelectProject = async projectId => {
    setLoading(true);
    try {
      await appAxios.post('/Project/v1.0/assignProjectToChat', { permitChatId, projectId });
      onSubmit({ projectId });
    } catch {
      toast.error('Failed to link project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = async customer => {
    setLoading(true);
    setSelectedCustomer(customer);
    await getProjectsByCustomer(customer._id);
    setView('projects');
    setLoading(false);
  };

  const handleCreateCustomer = async e => {
    e.preventDefault();
    setLoading(true);
    const newCustomer = await createCustomer(customerForm);
    setCustomerForm({ owner_name: '', owner_email: '', owner_phone: '', property_address: '', permit_chat_id: permitChatId });
    setTouched({ owner_email: false, owner_phone: false });
    if (newCustomer) {
      setSelectedCustomer(newCustomer);
      await getProjectsByCustomer(newCustomer._id);
      setView('projects');
    } else {
      await getCustomers(true);
      setView('customers');
    }
    setLoading(false);
  };

  const handleCreateProject = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      let newProjectId = null;
      if (isContractor && selectedCustomer) {
        const res = await appAxios.post('/Customer/v1.0/createCustomerProject', {
          projectName: projectForm.name,
          projectDescription: projectForm.description,
          CustomerId: selectedCustomer._id,
        });
        if (res.data.status === 'Success') {
          newProjectId = res.data.data?._id;
          setProjectForm({ name: '', description: '' });
        } else {
          toast.error(res.data.message || 'Failed to create project');
          return;
        }
      } else {
        const res = await appAxios.post('/Project/v1.0/createProject', {
          projectName: projectForm.name,
          projectDescription: projectForm.description,
          permitChatId,
        });
        newProjectId = res.data.data?._id;
        setProjectForm({ name: '', description: '' });
      }

      if (newProjectId) {
        await appAxios.post('/Project/v1.0/assignProjectToChat', { permitChatId, projectId: newProjectId });
        onSubmit({ projectId: newProjectId });
      } else {
        if (isContractor && selectedCustomer) {
          await getProjectsByCustomer(selectedCustomer._id);
        } else {
          await getProjects(true);
        }
        setView('projects');
      }
    } catch {
      toast.error('Failed to create project.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full bg-black border-gray-700 text-white placeholder-gray-500 text-sm focus:border-gray-500';
  const outlineBtn = 'w-full border border-gray-700 hover:border-gray-500 text-white py-2.5 rounded-lg transition text-sm';
  const primaryBtn =
    'flex-1 bg-[#7B7B7B] hover:bg-gray-500 text-white py-2.5 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Contractor: customer list
  if (view === 'customers') {
    return (
      <div className="space-y-3">
        <button
          onClick={() => setView('addCustomer')}
          className="w-full flex items-center justify-center gap-2 border border-gray-700 text-white py-2.5 rounded-lg hover:border-gray-500 transition text-sm"
        >
          <Plus className="w-4 h-4" />
          Add New Customer
        </button>
        <div className="max-h-[220px] overflow-y-auto space-y-2 pr-1 no-scrollbar">
          {customers?.length > 0 ? (
            customers.map(c => (
              <button
                key={c._id}
                onClick={() => handleSelectCustomer(c)}
                className="w-full text-left p-3 rounded-xl border border-gray-700 hover:bg-white/5 transition"
              >
                <div className="font-medium text-white text-sm">{c.owner_name}</div>
                <div className="text-xs text-gray-400 flex justify-between mt-0.5">
                  <span>{c.owner_email || 'N/A'}</span>
                  <span className="truncate max-w-[140px]">{c.property_address || 'N/A'}</span>
                </div>
              </button>
            ))
          ) : (
            <p className="text-center text-sm text-gray-500 py-4">No customers found</p>
          )}
        </div>
        {/* <button onClick={onBack} className={outlineBtn}>Back</button> */}
      </div>
    );
  }

  // Add customer form
  if (view === 'addCustomer') {
    return (
      <form onSubmit={handleCreateCustomer} className="space-y-3">
        <Input placeholder="Owner Name" value={customerForm.owner_name} onChange={e => setCustomerForm(p => ({ ...p, owner_name: e.target.value }))} required className={inputCls} />
        <Input
          placeholder="Owner Email"
          type="email"
          value={customerForm.owner_email}
          onBlur={() => setTouched(p => ({ ...p, owner_email: true }))}
          onChange={e => setCustomerForm(p => ({ ...p, owner_email: e.target.value }))}
          required
          className={inputCls}
        />
        {touched.owner_email && !isEmailValid && <p className="text-xs text-red-500">Enter a valid email address</p>}
        <Input
          placeholder="Owner Phone (10 digits)"
          maxLength={10}
          value={customerForm.owner_phone}
          onBlur={() => setTouched(p => ({ ...p, owner_phone: true }))}
          onChange={e => setCustomerForm(p => ({ ...p, owner_phone: e.target.value.replace(/\D/g, '') }))}
          required
          className={inputCls}
        />
        {touched.owner_phone && !isPhoneValid && <p className="text-xs text-red-500">Phone must be exactly 10 digits</p>}
        <Textarea
          placeholder="Property Address"
          value={customerForm.property_address}
          onChange={e => setCustomerForm(p => ({ ...p, property_address: e.target.value }))}
          required
          className={inputCls}
        />
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => {
              setView('customers');
              setCustomerForm({ owner_name: '', owner_email: '', owner_phone: '', property_address: '', permit_chat_id: permitChatId });
              setTouched({ owner_email: false, owner_phone: false });
            }}
            className="flex-1 border border-gray-700 text-white py-2.5 rounded-lg text-sm hover:border-gray-500 transition"
          >
            Back
          </button>
          <button type="submit" disabled={!isCustomerFormValid || loading} className={primaryBtn}>
            Create Customer
          </button>
        </div>
      </form>
    );
  }

  // Project list (homeowner + contractor after customer selected)
  if (view === 'projects') {
    return (
      <div className="space-y-3">
        {isContractor && selectedCustomer && (
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => setView('customers')}
              className="text-gray-400 hover:text-white flex items-center gap-1 transition"
            >
              <ArrowLeft className="w-3 h-3" /> Change Customer
            </button>
            <span className="text-gray-600">|</span>
            <span className="text-gray-300">{selectedCustomer.owner_name}</span>
          </div>
        )}
        <button
          onClick={() => setView('addProject')}
          className="w-full flex items-center justify-center gap-2 border border-gray-700 text-white py-2.5 rounded-lg hover:border-gray-500 transition text-sm"
        >
          <Plus className="w-4 h-4" />
          Create New Project
        </button>
        <div className="max-h-[200px] overflow-y-auto space-y-2 pr-1 no-scrollbar">
          {projects?.length > 0 ? (
            projects.map(p => (
              <button
                key={p._id}
                onClick={() => handleSelectProject(p._id)}
                className="w-full text-left p-3 rounded-xl border border-gray-700 hover:bg-white/5 transition"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-white text-sm truncate">{p.projectName}</div>
                    {p.projectDescription && (
                      <div className="text-xs text-gray-400 line-clamp-1">{p.projectDescription}</div>
                    )}
                  </div>
                </div>
              </button>
            ))
          ) : (
            <p className="text-center text-sm text-gray-500 py-4">No projects found</p>
          )}
        </div>
        {/* <button onClick={isContractor ? () => setView('customers') : onBack} className={outlineBtn}>Back</button> */}
      </div>
    );
  }

  // Add project form
  return (
    <form onSubmit={handleCreateProject} className="space-y-3">
      <Input
        placeholder="Project Name"
        value={projectForm.name}
        onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))}
        required
        className={inputCls}
      />
      <Textarea
        placeholder="Project Description (optional)"
        value={projectForm.description}
        onChange={e => setProjectForm(p => ({ ...p, description: e.target.value }))}
        className={inputCls}
      />
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={() => setView('projects')}
          className="flex-1 border border-gray-700 text-white py-2.5 rounded-lg text-sm hover:border-gray-500 transition"
        >
          Back
        </button>
        <button type="submit" disabled={!projectForm.name.trim() || loading} className={primaryBtn}>
          Create Project
        </button>
      </div>
    </form>
  );
};

export default ProjectLinkStep;
